import { useEffect, useMemo, useState, type JSX } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import { BindDialogReturn, DialogProps, DialogResult, DialogState, bindDialog, useDialogState } from "../../hooks/use-dialog-state/UseDialogState";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import Form from "../forms/form/Form";
import LoadingZone from "../loading-zone/LoadingZone";
import ResponsiveDialog from "../responsive-dialog/ResponsiveDialog";
import UnsavedChangesPrompt from "../unsaved-changes-prompt/UnsavedChangesPrompt";
import { CheckList, CheckListItemType } from "../check-list/CheckList";
import { useObjectViewState } from "../../queries/object-view-state/UseObjectViewState";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { IeLynxObject } from "../../common/models/eLynxObject";
import { IGroupViewState } from "../../services/state-data-service/Models";
import { useRoles } from "../../queries/user-role-administration/UseRoles";
import AssignHomePageDialogUtils from "./AssignHomePageDialogUtils";
import { AssignHomePageFieldType } from "./AssignHomePageFieldTypes";
import { ALL_ROLES_OPTION_ID, allRolesOption, RoleOptionType, useMembershipStepState } from "../../views/ui-customization-preset-admin-view/ui-customization-preset-editor/membership-step/MembershipStepState";
import SideSheet from "../side-sheet/side-sheet/SideSheet";
import SideSheetFilterInput from "../side-sheet/side-sheet-filter-input/SideSheetFilterInput";
import SideSheetSwitch from "../side-sheet/side-sheet-switch/SideSheetSwitch";
import SideSheetMultiSelect from "../side-sheet/side-sheet-multiselect/SideSheetMultiSelect";
import { AutocompleteChangeDetails, AutocompleteChangeReason, Button, Stack, Typography } from "@mui/material";
import { ContentContainer } from "../content-container/ContentContainer";
import { ContentSection } from "../content-section/ContentSection";
import { isMobile, isTablet } from "react-device-detect";
import { useUserFavoriteAccessibleUsers } from "../../queries/user-favorites/UseUserFavoriteAccessibleUsers";
import NoData from "../no-data/NoData";
import { IeLynxUser } from "../../common/models/eLynxUser";
import { IUIUserFavoriteModel } from "../../services/ui-personalization-data-service/Models";

export type AssignHomePageDialogInitValues = {
    title: string,
    favorite: IUIUserFavoriteModel;
    assignHomePage: boolean
};

export type AssignHomePageDialogResultValues = {
    users: IeLynxUser[];
};

export type AssignHomePageDialogResult = DialogResult<AssignHomePageDialogResultValues>;

export type AssignHomePageDialogProps = DialogProps<AssignHomePageDialogInitValues, AssignHomePageDialogResult>;

export default function AssignHomePageDialog({
    open,
    onClose,
    values
}: AssignHomePageDialogProps): JSX.Element {

    const [availableUsers, setAvailableUsers] = useState<CheckListItemType[]>([]);
    const [selectedIDs, setSelectedIDs] = useState<number[]>([]);

    const { t } = useTranslation();
    const { confirm } = useConfirmation();
    const routeContext = useRouteContext();

    const isPhone = isMobile && !isTablet;

    const [userState] = useUserContext();

    const {
        state: {
            filterValue,
            viewSelected,
            selectedRoles
        },
        setFilterValue,
        setViewSelected,
        setSelectedRoles,
        reset: resetState
    } = useMembershipStepState();

    const { data: objectViewState } = useQueryNotifications(useObjectViewState)({
        customerID: userState.customer?.id!,
        groupID: routeContext.groupID ?? -1,
        contextObject: {
            id: routeContext.groupID,
            objectType: eLynxObjectTypeEnum.Group
        } as IeLynxObject,
        enabled: routeContext.groupID !== undefined && open
    });

    const groupViewState = objectViewState ? objectViewState as IGroupViewState : undefined;

    const organizationID = groupViewState?.organizationID ?? -1;

    const {
        data: accessibleUsers,
        status,
    } = useQueryNotifications(useUserFavoriteAccessibleUsers)({
        groupID: routeContext.groupID as number,
        favoriteID: values?.favorite?.id ?? -1,
        enabled: open && values?.favorite !== undefined,
        options: {
            filterText: filterValue,
            filterRoleIDs: selectedRoles?.map(r => r.id) ?? [],
            //sortBy: UserSortEnum.LastNameAsc
        },
    });
    
    const {
        data: roles,
    } = useQueryNotifications(useRoles)({
        groupID: routeContext.groupID as number,
        organizationID: organizationID as number,
        enabled: organizationID !== undefined && open
    });

    const form = useForm<AssignHomePageFieldType>({
        mode: "onChange",
        defaultValues: {
            users: []
        } as AssignHomePageFieldType
    });

    const {
        handleSubmit,
        formState: {
            isValid,
            isDirty,
            
        },
        reset,
        control
    } = form;

    const {
        fields: formUsers,
        update
    } = useFieldArray<AssignHomePageFieldType, "users", "fieldKey">({ control: control, name: "users", keyName: "fieldKey" });

    const roleOptions = useMemo(() => {
        return [
            allRolesOption,
            ...roles?.data?.roles.map(r => {
                return {
                    id: r.id,
                    name: r.name ?? ""
                } as RoleOptionType;
            }) ?? []
        ];
    }, [
        roles?.data?.roles,
    ]);

    const selectedCount = selectedIDs.length;

    function onUserClicked(item: CheckListItemType) {
        updateUsers(item);
    }

    function updateUsers(item: CheckListItemType) {

        if (item.disabled) {
            return;
        }

        const userIndex = formUsers.findIndex(c => `${c.id}` === item.key);
        if (userIndex > -1) {

            const updatedUser = {...formUsers[userIndex]};

            updatedUser.selected = item.checked;

            update(userIndex, updatedUser);
        }
        
        let selectedItems = [...selectedIDs];

        if (item.checked) {
            selectedItems.push(item.id!);
        } else {

            selectedItems = selectedItems.filter(o => o !== item.id);
        }

        setSelectedIDs(selectedItems);
    }

    async function save(field: AssignHomePageFieldType) {

        onClose && onClose({
            reason: 'ok',
            values: {
                users: field.users.filter(o => o.selected)
            }
        });

        reset();

        resetState();

        setSelectedIDs([]);
    }

    async function onCancel() {

        const canCancel = isDirty
            ? await getCancelConfirmation()
            : true;

        if (canCancel) {
            reset();
            resetState();
            setSelectedIDs([]);

            onClose && onClose({
                reason: "cancel"
            });
        }
    }

    async function getCancelConfirmation() {
        return (await confirm({
            dialogTitle: t("cancelConfirmation.title"),
            confirmationText: t("cancelConfirmation.confirmationText"),
            yesButtonText: t("cancelConfirmation.yesButtonText"),
            noButtonText: t("cancelConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true
        })) === "yes";
    }



    function selectedRolesOnChange(
        _: React.SyntheticEvent,
        value: RoleOptionType[],
        reason: AutocompleteChangeReason,
        details?: AutocompleteChangeDetails<RoleOptionType> | undefined
    ) {
        const item = roleOptions.find(o => o.id === details?.option.id);

        if (item) {
            switch (reason) {
                case 'selectOption':
                    if (item.id === ALL_ROLES_OPTION_ID) {
                        setSelectedRoles(roleOptions.filter(o => o.id === ALL_ROLES_OPTION_ID));
                    } else {
                        setSelectedRoles(value.filter(role => role.id !== ALL_ROLES_OPTION_ID));
                    }
                    break;

                case 'removeOption':
                    setSelectedRoles(value.length === 0 ? [allRolesOption] : value);
                    break;

                default:
                    break;
            }
        }
    }
    function onSelectItems(checked: boolean) {

        let selectedItems: number[] = [];

        formUsers.forEach((o, userIndex) => {

            if (!o.disabled) {

                const updatedUser = {...formUsers[userIndex]};

                updatedUser.selected = checked;

                update(userIndex, updatedUser);

                if (checked) {
                    selectedItems.push(updatedUser.id);
                }
            }
        });

        setSelectedIDs(selectedItems);
    }

    function renderTitle() {
        
        return (
            <Stack direction='row'>
                <Typography variant='h6'>{values?.title}</Typography>

                <Button 
                    sx={{marginLeft: 'auto'}}
                    onClick={() => onSelectItems(true)}>
                    {t("buttons.selectAll")}
                </Button>
                <Button 
                    disabled={!selectedCount}
                    onClick={() => onSelectItems(false)}>
                    {t("buttons.clearAll")}
                </Button>                
            </Stack>
        )
    }

    useEffect(() => {

        if (open && accessibleUsers && values) {
            reset(AssignHomePageDialogUtils.mapToForm(accessibleUsers, values.favorite, values.assignHomePage));
        }

    }, [
        open,
        reset,
        accessibleUsers,
        values?.favorite
    ]);

    useEffect(() => {

        if (open) {

            const items = AssignHomePageDialogUtils.createAvailableUserCheckListItems(formUsers, selectedIDs);

            setAvailableUsers(items);
        }
        
    }, [
        open,
        formUsers,
        selectedIDs,
        setAvailableUsers
    ]);

    return (
        <>
            <ResponsiveDialog
                title={renderTitle()}
                enableOkay={
                    isValid && isDirty
                }
                okayLabel={t("buttons.save")}
                open={open}
                onClose={
                    (reason: string) => {
                        reason === "ok"
                            ? handleSubmit(async (data) => save(data))()
                            : onCancel();
                    }
                }
                contentsx={{
                    backgroundColor: "background.paper",
                    padding: 0,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    flex: "1 1 auto",
                    minHeight: !isPhone ? 600 : undefined
                }}
            >
                <LoadingZone isLoading={status === "loading"}>


                    <ContentContainer>
                        <ContentSection>
                            {
                                status === "success" && availableUsers.length === 0 &&
                                <NoData text="There are no users for the selected roles or that match the filter criteria." />
                            }

                            <Form form={form}>

                                <CheckList
                                    items={availableUsers}
                                    onItemClick={onUserClicked}
                                    filter={(item: CheckListItemType) => {
                                        return !viewSelected || item.checked;
                                    }}
                                />
                            </Form>

                        </ContentSection>
                        <SideSheet
                            title={t("options")}
                            open={true}
                        >
                            <SideSheetFilterInput
                                label={t("uiCustomizationPresetEditor.steps.membership.filter")}
                                value={filterValue}
                                onChange={(value: string) => {
                                    setFilterValue(value);
                                }}
                            />

                            <SideSheetSwitch
                                label={t("uiCustomizationPresetEditor.steps.membership.viewSelected")}
                                checked={viewSelected}
                                onChange={(checked: boolean) => {
                                    setViewSelected(checked);
                                }}
                            />

                            <SideSheetMultiSelect<RoleOptionType>
                                label={t("uiCustomizationPresetEditor.steps.membership.filterByRoles")}
                                placeholder=""
                                options={roleOptions}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                value={selectedRoles ?? []}
                                getOptionLabel={r => r.name}
                                shrinkLabel={true}
                                onChange={(event, value, reason, details) => {
                                    selectedRolesOnChange(event, value, reason, details);
                                }}
                            />
                        </SideSheet>

                    </ContentContainer>   
                </LoadingZone>

            </ResponsiveDialog>

            <UnsavedChangesPrompt
                hasChanges={isDirty}
                getUserConfirmation={getCancelConfirmation}
            />
        </>
    );
}

export type AssignHomePageDialogState = DialogState<AssignHomePageDialogInitValues, AssignHomePageDialogResult>;
export type BindAssignHomePageDialogReturn = BindDialogReturn<AssignHomePageDialogInitValues, AssignHomePageDialogResult>;

export function useAssignHomePageDialogState(): AssignHomePageDialogState {
    return useDialogState();
}

export function bindAssignHomePageDialog(props: AssignHomePageDialogState): BindAssignHomePageDialogReturn {
    return bindDialog(props);
}